import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { EglState } from '../../../store/reducers';
import { v2SelectFlowType } from '../../../store/selectors/order-entry-v2.selectors';
import { selectCartSegment, selectCurrentVirtualAgent } from '../../../store/selectors/user.selectors';
import { AgencyBranchForMonitoring } from '../../models/user/agent';
import { EglGeolocationService } from '../apttus/utility/egl-geolocation.service';
import { D365Service } from '../d365/d365.service';
import { LoggerService } from './logger.service';
import { PrivateConfigurationService } from './private-configuration.service';

@Injectable({
    providedIn: 'root'
})
export class ConsensoDoiDdlService {
    constructor(
        private configSrv: PrivateConfigurationService,
        private store: Store<EglState>,
        private d365Srv: D365Service,
        private logger: LoggerService,
        private geolocationService: EglGeolocationService,
        private router: Router
    ) {}

    skipConsensoDoi(): Observable<boolean> {
        const geoCfg = this.configSrv.config?.geolocation;
        if (!geoCfg?.featureFlag) return of(true);

        if (['/dashboard', '/allproducts'].includes(this.router.url)) {
            return of(true);
        }

        return combineLatest([this.store.select(v2SelectFlowType), this.store.select(selectCartSegment)]).pipe(
            take(1),
            switchMap(([flowType, cartSegment]) => {
                if (!geoCfg.operativeModeOn?.includes(flowType)) return of(true);
                // Nel caso si volesse escludere un segmento specifico, si può fare un check qui, ad esempio:
                //if (cartSegment === D365CustomerSegment.Microbusiness) return of(true);

                return this.geolocationService.isTablet().pipe(
                    switchMap((isTablet) => {
                        if (!isTablet) return of(true);

                        return this.d365Srv.isAgentInGeolocationWhitelist().pipe(
                            switchMap((isInWhitelist) => {
                                if (isInWhitelist) return of(true);

                                return this.isAgentAtOffice(geoCfg.toleranceMeters).pipe(
                                    // se è in ufficio => skip, altrimenti no
                                    map((isAtOffice) => isAtOffice)
                                );
                            })
                        );
                    })
                );
            })
        );
    }

    isAgentAtOffice(toleranceMeters: number): Observable<boolean> {
        return this.geolocationService.getTabletGeolocation().pipe(
            switchMap((geoRes) => {
                return this.store.select(selectCurrentVirtualAgent).pipe(
                    take(1),
                    map((agent) => ({ agent, geoRes }))
                );
            }),
            map(({ agent, geoRes }) => {
                if (!geoRes?.position) {
                    return false;
                }

                // Se non è valorizzato il CurrentAgencyBranch utilizzo il DefaultAgencyBranch US_443197
                const currentAgencyBranchAddress: AgencyBranchForMonitoring = {
                    AgencyBranchId:
                        agent?.CurrentAgencyBranch?.AgencyBranchId ?? agent?.DefaultAgencyBranch?.AgencyBranchId,
                    Name: agent?.CurrentAgencyBranch?.Name ?? agent?.DefaultAgencyBranch?.Name,
                    Latitude: agent?.CurrentAgencyBranch?.Latitude ?? agent?.DefaultAgencyBranch?.Latitude,
                    Longitude: agent?.CurrentAgencyBranch?.Longitude ?? agent?.DefaultAgencyBranch?.Longitude,
                    Province: agent?.CurrentAgencyBranch?.Province ?? agent?.DefaultAgencyBranch?.Province,
                    City: agent?.CurrentAgencyBranch?.City ?? agent?.DefaultAgencyBranch?.City,
                    ShortStreet: agent?.CurrentAgencyBranch?.ShortStreet ?? agent?.DefaultAgencyBranch?.Street
                };

                const latOffice = parseFloat(currentAgencyBranchAddress.Latitude?.replace(',', '.') ?? '');
                const lonOffice = parseFloat(currentAgencyBranchAddress.Longitude?.replace(',', '.') ?? '');

                if (isNaN(latOffice) || isNaN(lonOffice)) {
                    console.warn('📍Missing Agency coordinates');
                    return false;
                }

                const distance = this.geolocationService.distanceMeters(
                    latOffice,
                    lonOffice,
                    geoRes.position.latitude,
                    geoRes.position.longitude
                );

                return distance <= toleranceMeters;
            })
        );
    }
}
